/**
 * FadingBar - Shows/hides content based on scroll position with smooth transitions
 *
 * FadingNavigation - Generic wrapper for any navigation component with fading behavior
 */

import React, { useState, useEffect, useRef } from "react";

type FadingBarProps = {
  children: React.ReactNode;
  position: "top" | "bottom";
  className?: string;
  scrollThreshold?: number;
  bottomThreshold?: number;
  bottomOffset?: number; // e.g., 16, 32, 48 (in pixels)
  showOnScrollUp?: boolean;
  showAtBottom?: boolean;
  topOffset?: number; // e.g., 0, 16, 32 (in pixels)
  height?: number; // Optional manual height override (in pixels)
  contentPadding?: number; // Additional padding to add to content height (in pixels)
  notificationOffset?: number; // Additional offset when notifications are present (in pixels)
};

/**
 * FadingBar - Shows/hides content based on scroll position
 */
const FadingBar = ({
  children,
  position,
  className = "",
  scrollThreshold = 50,
  bottomThreshold = 10,
  bottomOffset = 0,
  topOffset = 48, // 3rem * 16px = 48px
  height,
  contentPadding = 0,
  showOnScrollUp = true,
  showAtBottom = true,
  notificationOffset = 0,
}: FadingBarProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);

  // Calculate effective height based on content or manual override
  const effectiveHeight = height ?? contentHeight + contentPadding;

  // Measure content height when component mounts or children change
  useEffect(() => {
    const measureContent = () => {
      if (contentRef.current) {
        const rect = contentRef.current.getBoundingClientRect();
        // Use pixels directly - no conversion needed
        setContentHeight(rect.height);
      }
    };

    // Measure immediately
    measureContent();

    // Set up ResizeObserver to watch for content size changes
    const resizeObserver = new ResizeObserver(measureContent);
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [children]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const isAtBottom =
        currentScrollY + windowHeight >= documentHeight - bottomThreshold;

      // Show when scrolling up or when at bottom of page
      if (
        (showOnScrollUp && currentScrollY < lastScrollY) ||
        (showAtBottom && isAtBottom)
      ) {
        setIsVisible(true);
      } else if (
        currentScrollY > lastScrollY &&
        currentScrollY > scrollThreshold
      ) {
        setIsVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [
    lastScrollY,
    scrollThreshold,
    bottomThreshold,
    showOnScrollUp,
    showAtBottom,
  ]);

  // Reset scroll position when component mounts
  useEffect(() => {
    setLastScrollY(0);
  }, []);

  const positionClasses =
    position === "top"
      ? "fixed left-0 right-0 z-30 bg-white border-b border-gray-200"
      : "fixed left-0 right-0 z-30 border-t border-gray-200";

  return (
    <>
      <div
        className={`${positionClasses} transition-opacity duration-300 ${
          isVisible ? "opacity-100" : "opacity-0 pointer-events-none"
        } ${className}`}
        style={{
          top: position === "top" ? `${topOffset}px` : undefined,
          bottom:
            position === "bottom"
              ? `${bottomOffset + notificationOffset}px`
              : undefined,
          // Remove height constraint - let content determine height naturally
        }}
      >
        <div ref={contentRef} className="w-full">
          {children}
        </div>
      </div>
      {/* Add background extension for bottom position with bottomOffset */}
      {position === "bottom" && (bottomOffset ?? 0) > 0 && (
        <div
          className={`fixed left-0 right-0 bottom-0 z-20 bg-white transition-opacity duration-300 ${
            isVisible ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          style={{
            height: `${bottomOffset + effectiveHeight}px`,
          }}
        />
      )}
      {position === "top" && <div className="h-16"></div>}
    </>
  );
};

/**
 * FadingNavigation - Generic wrapper for any navigation component with fading behavior
 */
type FadingNavigationProps = {
  children: React.ReactNode;
  position?: "top" | "bottom";
  className?: string;
  bottomOffset?: number;
  topOffset?: number;
  scrollThreshold?: number;
  bottomThreshold?: number;
  showOnScrollUp?: boolean;
  showAtBottom?: boolean;
  contentPadding?: number;
  notificationOffset?: number;
};

const FadingNavigation: React.FC<FadingNavigationProps> = ({
  children,
  position = "bottom",
  className = "",
  bottomOffset,
  topOffset,
  scrollThreshold,
  bottomThreshold,
  showOnScrollUp,
  showAtBottom,
  contentPadding,
  notificationOffset,
}) => {
  return (
    <FadingBar
      position={position}
      className={className}
      bottomOffset={bottomOffset}
      topOffset={topOffset}
      scrollThreshold={scrollThreshold}
      bottomThreshold={bottomThreshold}
      showOnScrollUp={showOnScrollUp}
      showAtBottom={showAtBottom}
      contentPadding={contentPadding}
      notificationOffset={notificationOffset}
    >
      {children}
    </FadingBar>
  );
};

export default FadingBar;
export { FadingNavigation };
